import { colors } from '@/constants/colors'
import {
	LoginCodeStatus,
	exchangeLoginCodeForToken,
	sendLoginCodeToEmail,
} from '@/services/auth'
import { useAuthStore } from '@/store'
import { AuthLoginState } from '@/store/auth/AuthenticationStore'
import { Button, ButtonText } from '@/ui/core/button'
import { Header } from '@/ui/core/header'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { type Href, router, useLocalSearchParams } from 'expo-router'
import { useRef, useState } from 'react'
import { StyleSheet, type TextStyle, type ViewStyle } from 'react-native'
import { OtpInput } from 'react-native-otp-entry'
import type { OtpInputRef } from 'react-native-otp-entry'
import Toast from 'react-native-toast-message'

const CODE_LENGTH = 6

const InvalidOtpCodeText = "That code didn't work. Try again."

const RateLimitedMessage =
	'You have made too many requests in a short period of time. Please wait a few minutes before trying again.'

// The code boxes take the Input's look: white, the brand's 12px corner, the same grey outline,
// Digital Blue while focused. The numeral is set in the display face, as figures are.
const otpStyles = StyleSheet.create({
	containerStyle: {
		gap: 8,
	},
	pinCode: {
		color: colors.ink,
		fontFamily: 'PPRightGrotesk-Medium',
		fontSize: 26,
	},
	filledInCode: {
		borderColor: colors.edge,
	},
	pinCodeContainer: {
		backgroundColor: colors.white,
		borderRadius: 12,
		borderCurve: 'continuous',
		borderColor: colors.edge,
		borderWidth: 1,
		flex: 1,
		height: 56,
	},
})

const SubmittedLoginForm = () => {
	const { loginEmail, setLoginState } = useAuthStore()
	const otpRef = useRef<OtpInputRef>(null)
	const [code, setCode] = useState('')
	const [isVerifying, setIsVerifying] = useState(false)
	const [isResending, setIsResending] = useState(false)

	const { redirectTo } = useLocalSearchParams<{ redirectTo?: string }>()

	const resetSubmission = () => {
		setLoginState(AuthLoginState.PENDING)
	}

	const submitOtpCode = async (value: string) => {
		if (value.length < CODE_LENGTH || isVerifying) return
		setIsVerifying(true)

		// Home, or the protected page that sent the member here. Replace, so back never returns
		// to the door.
		const onSuccess = () => {
			const destination = redirectTo && redirectTo !== '/login' ? redirectTo : '/'
			router.replace(destination as Href)
		}

		const onFailure = () => {
			otpRef?.current?.clear()
			setCode('')

			Toast.show({
				type: 'error',
				text1: InvalidOtpCodeText,
			})
		}

		await exchangeLoginCodeForToken(value, onSuccess, onFailure)
		setIsVerifying(false)
	}

	// A second code to the same address; without an address to send to, back to the first step.
	const resendCode = async () => {
		if (!loginEmail) {
			resetSubmission()
			return
		}
		setIsResending(true)
		const status = await sendLoginCodeToEmail(loginEmail)
		setIsResending(false)

		if (status === LoginCodeStatus.SUCCESSFUL) {
			otpRef?.current?.clear()
			setCode('')
			Toast.show({ type: 'success', text1: `We sent a new code to ${loginEmail}.` })
		} else if (status === LoginCodeStatus.RATE_LIMITED) {
			Toast.show({ type: 'error', text1: RateLimitedMessage })
		} else {
			Toast.show({
				type: 'error',
				text1: "We couldn't send a new code. Please go back and try again.",
			})
		}
	}

	return (
		<VStack gap={20}>
			<VStack gap={4} className="mb-6">
				<Header variant="h3" className="text-[40px] leading-[40px]">
					Check your email
				</Header>
				<Text tone="soft">We sent you a six-digit code.</Text>
			</VStack>

			<OtpInput
				ref={otpRef}
				numberOfDigits={CODE_LENGTH}
				focusColor={colors.focus}
				onTextChange={setCode}
				onFilled={submitOtpCode}
				theme={{
					containerStyle: otpStyles.containerStyle as ViewStyle,
					pinCodeContainerStyle: otpStyles.pinCodeContainer as ViewStyle,
					filledPinCodeContainerStyle: otpStyles.filledInCode as ViewStyle,
					pinCodeTextStyle: otpStyles.pinCode as TextStyle,
				}}
			/>

			<Button
				size="lg"
				fullWidth
				disabled={code.length < CODE_LENGTH}
				isLoading={isVerifying}
				onPress={() => submitOtpCode(code)}
			>
				<ButtonText>Continue</ButtonText>
			</Button>

			<HStack gap={20} className="items-center justify-center">
				<Button
					variant="link"
					size="sm"
					className="px-0"
					isLoading={isResending}
					onPress={resendCode}
				>
					<ButtonText>Resend code</ButtonText>
				</Button>
				<Button variant="link" size="sm" onPress={resetSubmission}>
					<ButtonText>Use a different email</ButtonText>
				</Button>
			</HStack>
		</VStack>
	)
}

export { SubmittedLoginForm }
